import React, { useState, memo } from "react";
import { Button, OverlayTrigger, Tooltip, Badge, Spinner } from "react-bootstrap";
import { Icon } from "@iconify/react";
import ProductRow from "./ProductRow";

const OrderRow = memo(({
  order,
  index,
  quantityView,
  expandQuantityView,
  onExpandQuantityViewChange,
  dispatchDatesMap,
  onDispatchDateChange,
  onSaveDispatchDate,
  savingDispatchId,
  dispatchDatePermission,
  actions,
  onEdit,
  onDelete,
  onQuantity,
  onStatusChange,
  onProductClick,
  onAddQueue,
  onMediaPreview,
  onViewLogs,
  onPartialDispatchClick,
  onStatusLabelClick,
  truncateText,
  formatDate,
  renderDateDifference,
  showCurrentDateDiff,
  statusOptions,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(!expanded);

  const progressPercentage = order.progressPercentage || 0;
  const totalCompletedPcs = order.totalCompletedPcs || 0;
  const quantityInPcs = order.quantityInPcs || 0;
  const remaining = quantityInPcs - totalCompletedPcs;

  const handleQuantityClick = (type) => {
    onQuantity(order, type);
  };

  const normalizeStatusValue = (value = "") =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const formatStatusLabel = (value = "") => {
    const normalized = normalizeStatusValue(value);
    if (normalized === "readytodispatch") return "Ready to Dispatch";
    if (normalized === "partialdispatch") return "Partial Dispatch";
    if (normalized === "new") return "New";
    if (normalized === "processing") return "Processing";
    if (normalized === "dispatch") return "Dispatch";
    if (normalized === "cancelled") return "Cancelled";
    return value;
  };

  const fallbackStatusOptions = [
    { value: "new", label: "New" },
    { value: "processing", label: "Processing" },
    { value: "readytodispatch", label: "Ready to Dispatch" },
    { value: "partialdispatch", label: "Partial Dispatch" },
    { value: "dispatch", label: "Dispatch" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const computedStatusOptions =
    Array.isArray(statusOptions) && statusOptions.length > 0
      ? statusOptions
      : fallbackStatusOptions;

  const currentStatusValue = normalizeStatusValue(order.status);

  const mergedStatusOptions = computedStatusOptions.some(
    (option) => option.value === currentStatusValue,
  )
    ? computedStatusOptions
    : [
        ...computedStatusOptions,
        {
          value: currentStatusValue,
          label: formatStatusLabel(order.status),
        },
      ];

  const getOptionStyle = (statusValue) => {
    const normalized = normalizeStatusValue(statusValue);
    if (normalized === "new") return { fontWeight: "bold", color: "#558B2F" };
    if (normalized === "processing") return { fontWeight: "bold", color: "#F9A825" };
    if (normalized === "readytodispatch") {
      return {
        fontWeight: "bold",
        color: progressPercentage >= 100 ? "#FBC02D" : "#999",
      };
    }
    if (normalized === "partialdispatch") return { fontWeight: "bold", color: "#546E7A" };
    if (normalized === "dispatch") {
      return {
        fontWeight: "bold",
        color: progressPercentage >= 100 ? "#80DEEA" : "#999",
      };
    }
    if (normalized === "cancelled") return { fontWeight: "bold", color: "#D32F2F" };
    return { fontWeight: "bold" };
  };

  const isOptionDisabled = (statusValue) => {
    const normalized = normalizeStatusValue(statusValue);
    if (normalized === "new") return progressPercentage > 0;
    if (normalized === "readytodispatch" || normalized === "dispatch") {
      return progressPercentage < 100;
    }
    return false;
  };


  console.log("order", order.partyName)

  return (
    <>
      <tr>
        <td title={`${order.orderId} - ${order.partyName}`}>
          <span className="cursor-pointer" onClick={toggleExpanded}>
            <div>
              <span className="fw-bold d-flex align-items-start">
                {truncateText(`${order?.orderId}   `)}
                <div>({order.product?.length || 0})</div>
              </span>
              <strong>{truncateText(order.partyName)}</strong>
            </div>
          </span>
        </td>
        <td className="truncate-cell" title={order.designColour}>
          <strong>{order.designColour || "-"}</strong>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <Button
              onClick={() => handleQuantityClick("decrease")}
              style={{
                width: "20px",
                height: "20px",
                fontSize: "14px",
                backgroundColor: "transparent",
                border: "1px solid rgb(0, 123, 255)",
                color: "#28a745",
                padding: 0,
                lineHeight: "1",
              }}
              className="me-1 d-flex justify-content-center align-items-center"
              disabled={totalCompletedPcs <= 0 || order.status?.toLowerCase() === "new"}
            >
              -
            </Button>
            <span
              style={{
                fontSize: "14px",
                minWidth: "50px",
                textAlign: "center",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {quantityView === "pcs"
                ? `${quantityInPcs?.toFixed(0)} (${remaining < 0 ? `+${Math.abs(remaining)}` : remaining})`
                : `${Math.abs(order?.quantityInSqft)?.toFixed(0)} (+${Math.abs(order?.remaining_quantity_in_sqft_pcs || 0)})`}
            </span>
            <Button
              onClick={() => handleQuantityClick("increase")}
              style={{
                width: "20px",
                height: "20px",
                fontSize: "14px",
                backgroundColor: "transparent",
                border: "1px solid rgb(0, 123, 255)",
                color: "#28a745",
                padding: 0,
                lineHeight: "1",
              }}
              className="ms-1 d-flex justify-content-center align-items-center"
            >
              +
            </Button>
          </div>
          <div style={{ marginTop: "15px" }}>
            <div className="d-flex flex-column gap-1 w-100 align-items-start">
              <div className="w-100">
                <div className="progress progress-sm rounded-pill">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Completed: {totalCompletedPcs} PCS<br />
                        {remaining < 0 ? `Extra: +${Math.abs(remaining)}` : `Remaining: ${remaining}`} PCS<br />
                        Total: {quantityInPcs} PCS
                      </Tooltip>
                    }
                  >
                    <div
                      className={`progress-bar rounded-pill ${progressPercentage >= 100 ? "bg-success" : "bg-primary-600"}`}
                      style={{ width: `${progressPercentage.toFixed(0)}%` }}
                    />
                  </OverlayTrigger>
                </div>
              </div>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Completed: {totalCompletedPcs} PCS<br />
                    {remaining < 0 ? `Extra: +${Math.abs(remaining)}` : `Remaining: ${remaining}`} PCS<br />
                    Total: {quantityInPcs} PCS
                  </Tooltip>
                }
              >
                <span className="text-secondary-light font-xs fw-semibold line-height-1 text-center">
                  {`${progressPercentage.toFixed(0)}%`}
                </span>
              </OverlayTrigger>
            </div>
          </div>
        </td>
        <td className="truncate" title={`${order.orderDate} & ${order.tentative_date}`}>
          <strong>
            {formatDate(order.orderDate)} & {formatDate(order.tentative_date)}
          </strong>
          <br />
          {renderDateDifference(order.orderDate, order.tentative_date, showCurrentDateDiff)}
        </td>
        <td>
          <div className="d-flex flex-column" style={{ gap: 6 }}>
            <div className="d-flex align-items-center gap-2">

              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 140 }}
                value={dispatchDatesMap[order.orderId] || normalizeDateForInput(order.tentative_date || order.orderDate || "")}
                onChange={(e) => onDispatchDateChange(order.orderId, e.target.value)}
                disabled={!dispatchDatePermission}
              />
              {dispatchDatePermission && (
                <button
                  className="btn btn-sm btn-primary"
                  style={{ padding: "0.25rem 0.5rem", minWidth: 56, fontSize: "0.8rem", height: 32 }}
                  onClick={() => {
                    const dateValue = dispatchDatesMap[order.orderId] || normalizeDateForInput(order.tentative_date || order.orderDate || "");
                    console.log("Saving dispatch date:", {
                      orderId: order.orderId,
                      date: dateValue,
                      id: order._id
                    });
                    onSaveDispatchDate(order.orderId, dateValue, order._id);
                  }}
                  disabled={savingDispatchId === order.orderId}
                >
                  {savingDispatchId === order.orderId ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Save"
                  )}
                </button>
              )}
            </div>
            {order.old_tentative_dates?.length > 0 && (
              <div style={{ marginTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#b8860b", display: "inline-block" }}>
                  {formatDate(order.old_tentative_dates[order.old_tentative_dates.length - 1].old_tentative_date)}
                </span>
              </div>
            )}
          </div>
        </td>
        <td>
          <select
            className="form-select custom-select-border"
            value={currentStatusValue}
            data-order-index={index}
            onChange={(e) => onStatusChange(e, index, order)}
            style={{
              fontSize: "0.75rem",
              height: "2rem",
              lineHeight: "2rem",
              padding: "0 0.5rem",
              textAlign: "center",
              maxWidth: "150px",
              fontWeight: "bold",
            }}
          >
            {mergedStatusOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={isOptionDisabled(option.value)}
                style={getOptionStyle(option.value)}
              >
                {formatStatusLabel(option.label)}
              </option>
            ))}
          </select>
          {order.total_partial_dispatch_quantity > 0 && (
            <Badge
              pill
              bg="primary"
              style={{ cursor: "pointer", fontSize: "0.6rem", marginTop: "5px" }}
              onClick={() => onStatusLabelClick(order)}
            >
              Partial Dispatch
            </Badge>
          )}
        </td>
        <td>
          {actions?.map((action, actionIndex) => (
            <button
              key={actionIndex}
              className={typeof action.className === "function" ? action.className(order) : action.className}
              onClick={() => {
                if (action.icon === "lucide:edit") onEdit(order);
                else if (action.icon === "mingcute:delete-2-line") onDelete(order);
                else if (action.icon === "mdi:history") onViewLogs(order);
                else action.handler?.(order);
              }}
              disabled={action.disabled ? action.disabled(order) : false}
            >
              <Icon icon={action.icon} />
            </button>
          ))}
        </td>
      </tr>

      {expanded && (
        <tr className="expanded-row">
          <td colSpan="7">
            <div className="table-responsive">
              <table className="table table-bordered compact-table" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th className="table-body-text">ID</th>
                    <th className="table-body-text">Design Name</th>
                    <th className="table-body-text">Color Name</th>
                    <th>
                      <div className="d-flex flex-column">
                        <span className="mb-2 table-header-text">QTY</span>
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontSize: "11px" }}>Pieces</span>
                            <div className="form-switch d-flex align-items-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={expandQuantityView === "sqft"}
                                onChange={() => onExpandQuantityViewChange(expandQuantityView === "pcs" ? "sqft" : "pcs")}
                                style={{ backgroundColor: "#dee2e6" }}
                              />
                            </div>
                            <span style={{ fontSize: "11px" }}>Sqft</span>
                          </div>
                        </div>
                      </div>
                    </th>
                    <th className="table-body-text">Progress</th>
                    <th className="table-body-text">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {order.product?.map((product, idx) => (
                    <ProductRow
                      key={`${product.product_id}-${idx}`}
                      product={{ ...product, orderId: order.orderId }}
                      orderId={order.orderId}
                      masterId={order.masterId}
                      quantityView={expandQuantityView}
                      onProductClick={onProductClick}
                      onAddQueue={onAddQueue}
                      onMediaPreview={onMediaPreview}
                      truncateText={truncateText}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

// Helper function
const normalizeDateForInput = (dateString) => {
  if (!dateString) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

  const parsed = new Date(dateString);
  if (!isNaN(parsed)) {
    return parsed.toISOString().split('T')[0];
  }

  const parts = dateString.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})$/);
  if (parts) {
    return `${parts[3]}-${parts[2]}-${parts[1]}`;
  }

  return "";
};

export default OrderRow;